import { initializeApp } from "firebase/app";

// firebase config
// TODO: Replace the following with your app's Firebase project configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);

// end of firebase config

export const currencyFormatter = ({
  amount,
  currency = "USD",
}: {
  amount: number | undefined;
  currency?: string;
}) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(amount ?? 0);
};

export const getVideoType = (url: string | undefined | null) => {
  if (!url || typeof url !== "string") return "video/mp4";

  const extension = url.split(".").pop()?.split("?")[0]?.toLowerCase();

  const videoTypes = {
    mp4: "video/mp4",
    webm: "video/webm",
    ogg: "video/ogg",
    mov: "video/quicktime",
    m4v: "video/mp4",
  };

  return videoTypes[extension as keyof typeof videoTypes] || "video/mp4";
};

export type DevicePlatform = "ios" | "android" | "desktop" | "unknown";

export type DeviceInfo = {
  platform: DevicePlatform;
  isMobile: boolean;
};

type LaunchAppOptions = {
  deepLink: string;
  fallbackUrl: string;
  fallbackDelayMs?: number;
  onFallback?: () => void;
};

const MOBILE_REGEX = /Mobi|Android|iP(ad|hone|od)/i;
const ANDROID_REGEX = /Android/i;
const IOS_REGEX = /iPhone|iPad|iPod/i;

export function detectDevice(userAgent?: string): DeviceInfo {
  const ua =
    userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");

  const isAndroid = ANDROID_REGEX.test(ua);
  const isIOS = IOS_REGEX.test(ua);
  const isMobile = MOBILE_REGEX.test(ua);

  if (isAndroid) {
    return { platform: "android", isMobile: true };
  }

  if (isIOS) {
    return { platform: "ios", isMobile: true };
  }

  if (ua && !isMobile) {
    return { platform: "desktop", isMobile: false };
  }

  return { platform: "unknown", isMobile };
}

export function launchAppOrFallback({
  deepLink,
  fallbackUrl,
  fallbackDelayMs = 100,
  onFallback,
}: LaunchAppOptions) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  let fallbackHandled = false;
  let fallbackTimerId: number | undefined;

  const cancelFallback = () => {
    if (fallbackHandled) {
      return;
    }

    fallbackHandled = true;

    if (fallbackTimerId) {
      window.clearTimeout(fallbackTimerId);
    }

    document.removeEventListener("visibilitychange", handleAppTransition);
    window.removeEventListener("blur", handleAppTransition);
    window.removeEventListener("pagehide", handleAppTransition);
  };

  const handleAppTransition = () => {
    if (document.visibilityState === "hidden" || !document.hasFocus()) {
      cancelFallback();
    }
  };

  document.addEventListener("visibilitychange", handleAppTransition);
  window.addEventListener("blur", handleAppTransition);
  window.addEventListener("pagehide", handleAppTransition);

  window.location.assign(deepLink);

  fallbackTimerId = window.setTimeout(() => {
    if (fallbackHandled) {
      return;
    }

    cancelFallback();

    if (document.visibilityState !== "visible" || !document.hasFocus()) {
      return;
    }

    if (onFallback) {
      onFallback();
      return;
    }

    window.open(fallbackUrl, "_blank", "noopener");
  }, fallbackDelayMs);
}
